/**
 * API Client for V2
 *
 * The public name for the configured axios instance in `@/lib/agent` — import
 * from here, not from the agent directly. What it does (auth injection, retry,
 * 401/403 handling, error enrichment, metrics, timeout) is documented there,
 * in one place next to the code that implements it.
 *
 * Usage in query functions:
 * ```tsx
 * import { apiClient } from '@/api/client';
 *
 * const fetchProcesses = async () => {
 *   const { data } = await apiClient.post('/processes/all');
 *   return data.docs;
 * };
 * ```
 *
 * Performance monitoring:
 * ```tsx
 * import { onRequestComplete } from '@/api/client';
 *
 * // Register a performance callback
 * const unsubscribe = onRequestComplete((metrics) => {
 *   console.log('Request completed:', metrics);
 *   // Send to analytics, etc.
 * });
 *
 * // Clean up when done
 * unsubscribe();
 * ```
 */

import agent, { onRequestComplete } from '@/lib/agent';

export const apiClient = agent;

// Export performance monitoring hook
export { onRequestComplete };

// Re-export axios types for convenience
export type { AxiosError, AxiosResponse } from 'axios';

// Re-export agent types
export type {
  ApiError,
  RequestMetrics,
  PerformanceCallback,
  AgentConfig,
  RetryConfig
} from '@/lib/agent.types';
